import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Col,
  Container,
  Input,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import {
  FiEye,
  FiTrash2,
  FiAlertTriangle,
} from "react-icons/fi";
import CommonHeader from "components/Headers/CommonHeader";
import { FiGrid } from "react-icons/fi";
import { FaEdit } from "react-icons/fa";
import {
  getalluserdetails,
  getuserdetailsbyid,
  adduserdetails,
  updateuserdetails,
  deleteuserdetails,
  sendUserWarning,
} from "../../api/ApiServices";
import Swal from "sweetalert2";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import { getNames } from "country-list";

const UserTable = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalUsers: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });


  const [summary, setSummary] = useState({
    total: 0,
    totalGuides: 0,
    totalTravelers: 0,
  });



  // Add User Modal States
  const [addModal, setAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    password: "",
    confirmPassword: "",
    country: "",
    type: "traveler",
    gender: "",
    latitude: "",
    longitude: "",
    location: "",
    createdFrom: "admin",
    emailVerified: false,
  });

  const [addLoading, setAddLoading] = useState(false);
  const [isGeocodingAdd, setIsGeocodingAdd] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showAddConfirmPassword, setShowAddConfirmPassword] = useState(false);

  // View User Modal States
  const [viewModal, setViewModal] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Edit User Modal States
  const [editModal, setEditModal] = useState(false);
  const [editUser, setEditUser] = useState({
    uuid: "",
    fullName: "",
    email: "",
    mobileNumber: "",
    country: "",
    type: "traveler",
    gender: "",
    latitude: "",
    longitude: "",
    location: "",
    isActive: true,
    createdFrom: "",
    emailVerified: false,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [isGeocodingEdit, setIsGeocodingEdit] = useState(false);

  // Warning User Modal States
  const [warningModal, setWarningModal] = useState(false);
  const [warningLoading, setWarningLoading] = useState(false);
  const [warningData, setWarningData] = useState({
    userUuid: "",
    fullName: "",
    email: "",
    type: "",
    subject: "Warning Notice - Area Trek Account Notification",
    message: "",
  });

  // ==============================================
  // FETCH USERS
  // ==============================================

  const fetchUsers = useCallback(
    async (page = 1, limit = 10, search = "") => {
      setLoading(true);

      try {
        const statusParam =
          statusFilter === "Active"
            ? "active"
            : statusFilter === "Pending"
              ? "inactive"
              : "";

        const typeParam =
          typeFilter === "All" ? "" : typeFilter.toLowerCase();

        const response = await getalluserdetails(
          page,
          limit,
          search,
          statusParam,
          typeParam
        );

        console.log("Users Response:", response);

        if (response && response.success) {
          // Users list
          setUsers(response.data || []);

          // Pagination
          if (response.pagination) {
            setPagination(response.pagination);
          }

          // Summary
          if (response.summary) {
            setSummary({
              total: response.summary.total || 0,
              totalGuides: response.summary.totalGuides || 0,
              totalTravelers: response.summary.totalTravelers || 0,
            });
          } else {
            setSummary({
              total: 0,
              totalGuides: 0,
              totalTravelers: 0,
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.message || "Failed to fetch users",
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);

        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error?.response?.data?.message ||
            "Something went wrong",
        });
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, typeFilter]
  );
  // ==============================================
  // FETCH USER BY ID
  // ==============================================

  const fetchUserById = async (uuid) => {
    setViewLoading(true);
    try {
      const response = await getuserdetailsbyid(uuid);
      console.log("User Details Response:", response);

      if (response && response.success) {
        // Get the user data
        const userData = response.data;

        // S3 Base URL for your images
        const S3_BASE_URL = "https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/";

        // Create a new object with full image URLs
        const userWithFullImages = {
          ...userData,
          // Construct full URLs for images
          profileImageFull: userData.profileImage ? `${S3_BASE_URL}${userData.profileImage}` : null,
          coverImageFull: userData.coverImage ? `${S3_BASE_URL}${userData.coverImage}` : null,
          // Keep original fields for reference
          profileImage: userData.profileImage,
          coverImage: userData.coverImage,
        };

        // Set the user with full image URLs
        setViewUser(userWithFullImages);
        setViewModal(true);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to fetch user details",
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setViewLoading(false);
    }
  };

  // ==============================================
  // ADD USER
  // ==============================================

  const handleAddUser = async () => {
    if (!newUser.fullName) {
      Swal.fire("Error", "Please enter full name", "error");
      return;
    }
    if (!newUser.email) {
      Swal.fire("Error", "Please enter email", "error");
      return;
    }
    if (!newUser.mobileNumber) {
      Swal.fire("Error", "Please enter mobile number", "error");
      return;
    }
    if (!newUser.password) {
      Swal.fire("Error", "Please enter password", "error");
      return;
    }
    if (newUser.password !== newUser.confirmPassword) {
      Swal.fire("Error", "Passwords do not match", "error");
      return;
    }

    setAddLoading(true);
    try {
      // Do not send createdFrom/emailVerified from frontend; backend manages these
      const payload = {
        fullName: newUser.fullName,
        email: newUser.email,
        mobileNumber: newUser.mobileNumber,
        password: newUser.password,
        confirmPassword: newUser.confirmPassword,
        country: newUser.country,
        type: newUser.type,
        gender: newUser.gender,
        latitude: newUser.latitude,
        longitude: newUser.longitude,
        location: newUser.location,
      };

      const response = await adduserdetails(payload);
      console.log("Add User Response:", response);

      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "User added successfully",
        });
        setAddModal(false);
        setNewUser({
          fullName: "",
          email: "",
          mobileNumber: "",
          password: "",
          confirmPassword: "",
          country: "",
          type: "traveler",
          gender: "",
          latitude: "",
          longitude: "",
          location: "",
        });

        fetchUsers(pagination.currentPage, pagination.limit, searchTerm);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to add user",
        });
      }
    } catch (error) {
      console.error("Error adding user:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Something went wrong",
      });
    } finally {
      setAddLoading(false);
    }
  };

  // ==============================================
  // DELETE USER
  // ==============================================

  const handleDeleteUser = async (uuid) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (confirmResult.isConfirmed) {
      try {
        const response = await deleteuserdetails(uuid);
        console.log("Delete User Response:", response);

        if (response && response.success) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "User has been deleted.",
          });
          fetchUsers(pagination.currentPage, pagination.limit, searchTerm);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.message || "Failed to delete user",
          });
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || "Something went wrong",
        });
      }
    }
  };

  // ==============================================
  // EDIT USER
  // ==============================================

  const handleEditClick = (user) => {
    console.log("Editing user:", user);
    console.log("isActive:", user.isActive);

    setEditUser({
      uuid: user.uuid || "",
      fullName: user.fullName || "",
      email: user.email || "",
      mobileNumber: user.mobileNumber || "",
      country: user.country || "",
      type: user.type || "traveler",
      gender: user.gender || "",
      latitude: user.latitude ?? "",
      longitude: user.longitude ?? "",
      location: user.location || "",
      isActive: user.isActive === true,
      createdFrom: user.createdFrom || "",
      emailVerified: user.emailVerified === true,
    });

    setEditModal(true);
  };


  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditUser((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ==============================================
  // UPDATE USER - FIXED
  // ==============================================

  const handleUpdateUser = async () => {
    // Validation
    if (!editUser.fullName) {
      Swal.fire("Error", "Please enter full name", "error");
      return;
    }
    if (!editUser.email) {
      Swal.fire("Error", "Please enter email", "error");
      return;
    }
    if (!editUser.mobileNumber) {
      Swal.fire("Error", "Please enter mobile number", "error");
      return;
    }

    setEditLoading(true);
    try {
      // Create update data object with all fields
      const updateData = {
        uuid: editUser.uuid,
        fullName: editUser.fullName,
        email: editUser.email,
        mobileNumber: editUser.mobileNumber,
        country: editUser.country,
        type: editUser.type,
        gender: editUser.gender,
        latitude: editUser.latitude,
        longitude: editUser.longitude,
        location: editUser.location,
        isActive: editUser.isActive,
      };

      // API call - passing only the data object
      const response = await updateuserdetails(updateData);
      console.log("Update User Response:", response);

      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "User updated successfully",
        });
        setEditModal(false);
        fetchUsers(pagination.currentPage, pagination.limit, searchTerm);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to update user",
        });
      }
    } catch (error) {
      console.error("Error updating user:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Something went wrong",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // ==============================================
  // WARNING USER
  // ==============================================

  const handleWarningClick = (user) => {
    setWarningData({
      userUuid: user.uuid || "",
      fullName: user.fullName || "User",
      email: user.email || "",
      type: user.type || "traveler",
      subject: "Warning Notice - Area Trek Account Notification",
      message: "",
    });
    setWarningModal(true);
  };

  const handleWarningInputChange = (e) => {
    const { name, value } = e.target;
    setWarningData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSendWarning = async () => {
    if (!warningData.message || !warningData.message.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Message Required",
        text: "Please enter a warning message to send to the user.",
      });
      return;
    }

    setWarningLoading(true);
    try {
      const response = await sendUserWarning({
        userUuid: warningData.userUuid,
        subject: warningData.subject,
        message: warningData.message.trim(),
      });

      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Warning Sent!",
          text: response.message || "Warning has been recorded and emailed to the user successfully.",
        });
        setWarningModal(false);
        setWarningData({
          userUuid: "",
          fullName: "",
          email: "",
          type: "",
          subject: "Warning Notice - Area Trek Account Notification",
          message: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to send warning.",
        });
      }
    } catch (error) {
      console.error("Error sending user warning:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to send warning. Please check network and email server settings.",
      });
    } finally {
      setWarningLoading(false);
    }
  };

  // ==============================================
  // EFFECTS
  // ==============================================

  useEffect(() => {
    fetchUsers(1, 10, "");
  }, [fetchUsers]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchUsers(1, pagination.limit, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, fetchUsers, pagination.limit]);

  // ==============================================
  // REVERSE GEOCODING HELPER
  // ==============================================

  const fetchReverseGeocode = async (lat, lng) => {
    if (lat === "" || lng === "" || lat === null || lng === undefined) return null;
    const numLat = parseFloat(lat);
    const numLng = parseFloat(lng);
    if (isNaN(numLat) || isNaN(numLng)) return null;
    if (numLat < -90 || numLat > 90 || numLng < -180 || numLng > 180) return null;

    try {
      // Primary: BigDataCloud Reverse Geocoding API
      const res = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${numLat}&longitude=${numLng}&localityLanguage=en`
      );
      if (res.ok) {
        const data = await res.json();
        const city =
          data.city ||
          data.locality ||
          (data.localityInfo?.administrative &&
            data.localityInfo.administrative[2]?.name);
        const state = data.principalSubdivision;
        const country = data.countryName;
        const parts = [city, state, country].filter(Boolean);
        if (parts.length > 0) {
          return {
            location: parts.join(", "),
            country: country || "",
          };
        }
      }
    } catch (err) {
      console.warn("BigDataCloud reverse geocode error:", err);
    }

    try {
      // Fallback: OpenStreetMap Nominatim API
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${numLat}&lon=${numLng}`
      );
      if (res.ok) {
        const data = await res.json();
        if (data && data.address) {
          const addr = data.address;
          const city =
            addr.city ||
            addr.town ||
            addr.village ||
            addr.suburb ||
            addr.county ||
            addr.district;
          const state = addr.state;
          const country = addr.country;
          const parts = [city, state, country].filter(Boolean);
          const locationStr =
            parts.length > 0 ? parts.join(", ") : data.display_name || "";
          if (locationStr) {
            return {
              location: locationStr,
              country: country || "",
            };
          }
        }
      }
    } catch (err) {
      console.warn("Nominatim reverse geocode error:", err);
    }

    return null;
  };

  // Auto Reverse Geocoding Effect for Add User Modal
  useEffect(() => {
    if (!addModal) return;
    const lat = newUser.latitude;
    const lng = newUser.longitude;
    if (
      lat === "" ||
      lng === "" ||
      lat === undefined ||
      lng === undefined ||
      isNaN(parseFloat(lat)) ||
      isNaN(parseFloat(lng))
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      setIsGeocodingAdd(true);
      const result = await fetchReverseGeocode(lat, lng);
      if (result && result.location) {
        setNewUser((prev) => ({
          ...prev,
          location: result.location,
          country: prev.country ? prev.country : result.country || prev.country,
        }));
      }
      setIsGeocodingAdd(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [newUser.latitude, newUser.longitude, addModal]);

  // Auto Reverse Geocoding Effect for Edit User Modal
  useEffect(() => {
    if (!editModal) return;
    const lat = editUser.latitude;
    const lng = editUser.longitude;
    if (
      lat === "" ||
      lng === "" ||
      lat === undefined ||
      lng === undefined ||
      isNaN(parseFloat(lat)) ||
      isNaN(parseFloat(lng))
    ) {
      return;
    }

    const timer = setTimeout(async () => {
      setIsGeocodingEdit(true);
      const result = await fetchReverseGeocode(lat, lng);
      if (result && result.location) {
        setEditUser((prev) => ({
          ...prev,
          location: result.location,
          country: prev.country ? prev.country : result.country || prev.country,
        }));
      }
      setIsGeocodingEdit(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [editUser.latitude, editUser.longitude, editModal]);

  useEffect(() => {
    fetchUsers(1, pagination.limit, searchTerm);
  }, [statusFilter, typeFilter]);

  // ==============================================
  // HANDLERS
  // ==============================================

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchUsers(newPage, pagination.limit, searchTerm);
    }
  };

  const handleViewUser = (userItem) => {
    if (typeof userItem === "object" && userItem !== null) {
      setViewUser(userItem);
      setViewModal(true);
      fetchUserById(userItem.uuid);
    } else {
      fetchUserById(userItem);
    }
  };

  const toggleAddModal = () => {
    setAddModal(!addModal);
    setShowAddPassword(false);
    setShowAddConfirmPassword(false);

    if (!addModal) {
      setNewUser({
        fullName: "",
        email: "",
        mobileNumber: "",
        password: "",
        confirmPassword: "",
        country: "",
        type: "traveler",
        gender: "",
        latitude: "",
        longitude: "",
        location: "",
        createdFrom: "admin",
        emailVerified: false,
      });
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==============================================
  // SUMMARY CALCULATION
  // ==============================================

  // const summary = useMemo(() => {
  //   return {
  //     total: pagination.totalUsers || 0,
  //     totalGuides: pagination.totalGuides || 0,
  //     totalTravelers: pagination.totalTravelers || 0,
  //   };
  // }, [
  //   pagination.totalUsers,
  //   pagination.totalGuides,
  //   pagination.totalTravelers,
  // ]);

  // ==============================================
  // FILTERED USERS
  // ==============================================

  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (statusFilter !== "All") {
      filtered = filtered.filter((user) => {
        if (statusFilter === "Active") return user.isActive === true;
        if (statusFilter === "Pending") return user.isActive === false;
        return true;
      });
    }

    return filtered;
  }, [users, statusFilter]);

  // ==============================================
  // HELPER FUNCTIONS
  // ==============================================

  const getStatusColor = (isActive) => {
    if (isActive === true) return "success";
    return "warning";
  };

  const getStatusText = (isActive) => {
    return isActive ? "Active" : "Pending";
  };

  const getTypeColor = (type) => {
    if (type === "traveler") return "info";
    if (type === "guide") return "primary";
    return "secondary";
  };
  const getTypeBadgeClass = (type) => {
    if (type === "traveler") return "badge-traveler";
    if (type === "guide") return "badge-guide";
    return "badge-default";
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive ? "badge-active" : "badge-inactive";
  };


  const COUNTRY_OPTIONS = getNames().map((country) => ({
    label: country,
    value: country,
  }));
  // ==============================================
  // UI
  // ==============================================

  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        {/* SUMMARY CARDS */}
        <Row className="mb-4">
          {/* Total Users */}
          <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
            <Card
              className="card-stats shadow-sm border-0 h-100 rounded-lg"
              role="button"
              tabIndex={0}
              onClick={() => setTypeFilter("All")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setTypeFilter("All");
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  typeFilter === "All"
                    ? "2px solid #5e72e4"
                    : "1px solid transparent",
                transform: typeFilter === "All" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="py-3 px-3 px-sm-4">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Total Users
                    </span>
                    <span className="h2 font-weight-bold mb-0 text-dark">
                      {summary?.total ?? 0}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                      <i className="fas fa-users" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Total Guides */}
          <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
            <Card
              className="card-stats shadow-sm border-0 h-100 rounded-lg"
              role="button"
              tabIndex={0}
              onClick={() => setTypeFilter("guide")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setTypeFilter("guide");
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  typeFilter === "guide"
                    ? "2px solid #2dce89"
                    : "1px solid transparent",
                transform: typeFilter === "guide" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="py-3 px-3 px-sm-4">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Total Guides
                    </span>
                    <span className="h2 font-weight-bold mb-0 text-success">
                      {summary?.totalGuides ?? 0}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                      <i className="fas fa-user-tie" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>

          {/* Total Travelers */}
          <Col xs="12" sm="4" md="4" className="mb-3 mb-sm-0">
            <Card
              className="card-stats shadow-sm border-0 h-100 rounded-lg"
              role="button"
              tabIndex={0}
              onClick={() => setTypeFilter("traveler")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setTypeFilter("traveler");
                }
              }}
              style={{
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                border:
                  typeFilter === "traveler"
                    ? "2px solid #11cdef"
                    : "1px solid transparent",
                transform:
                  typeFilter === "traveler" ? "translateY(-3px)" : "none",
              }}
            >
              <CardBody className="py-3 px-3 px-sm-4">
                <Row className="align-items-center">
                  <div className="col">
                    <span className="text-muted text-xs text-uppercase font-weight-bold d-block mb-1">
                      Total Travelers
                    </span>
                    <span className="h2 font-weight-bold mb-0 text-info">
                      {summary?.totalTravelers ?? 0}
                    </span>
                  </div>
                  <Col className="col-auto">
                    <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                      <i className="fas fa-suitcase-rolling" />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* USER TABLE */}
        <Card className="shadow border-0">
          <CardHeader className="bg-white border-0 pb-0 pt-4 px-3 px-sm-4">
            <Row className="align-items-center justify-content-between">
              <Col xs="auto">
                <h3 className="mb-0 font-weight-bold text-dark">User Management</h3>
              </Col>
              <Col xs="auto" className="text-right">
                <Button
                  size="sm"
                  onClick={toggleAddModal}
                  style={{
                    color: "var(--background-white)",
                  }}
                  className="bg-button-gradient-primary"
                >
                  + Add User
                </Button>
              </Col>
            </Row>
          </CardHeader>

          <CardBody className="px-3 px-sm-4 py-3">
            <Row className="align-items-center mb-3">
              <Col xs="12" md="6" lg="5" className="mb-2 mb-md-0">
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search" />
                    </InputGroupText>
                  </InputGroupAddon>

                  <Input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search user by name or email..."
                  />
                </InputGroup>
              </Col>
              <Col xs="12" sm="6" md="3" lg="3" className="mb-2 mb-sm-0">
                <CustomDropdown
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { label: "All Status", value: "All" },
                    { label: "Active", value: "Active" },
                    { label: "Pending", value: "Pending" },
                  ]}
                />
              </Col>

              <Col xs="12" sm="6" md="3" lg="2">
                <CustomDropdown
                  value={typeFilter}
                  onChange={(value) => {
                    setTypeFilter(value);
                    fetchUsers(
                      1,
                      pagination.limit,
                      searchTerm
                    );
                  }}
                  options={[
                    { label: "All Types", value: "All" },
                    { label: "Traveler", value: "traveler" },
                    { label: "Guide", value: "guide" },
                  ]}
                />
              </Col>
            </Row>

            {/* Table */}
            <div className="table-responsive">
              {loading ? (
                <div className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                  <p className="mt-2">Loading users...</p>
                </div>
              ) : (
                <>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>S.No.</th>
                        <th>User</th>
                        <th>Type</th>
                        <th>Mobile</th>
                        <th>Country</th>
                        <th>Status</th>
                        <th>Created From</th>
                        <th>Email Verified</th>
                        <th className="text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user, index) => (

                          <tr key={user._id || user.uuid}>
                            <td>
                              {(pagination.currentPage - 1) * pagination.limit + index + 1}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="guide-avatar mr-2">
                                  {user.profileImage ? (
                                    <img
                                      src={`https://areatrek-live-bucket.s3.us-east-2.amazonaws.com/users-profile-images/${user.profileImage}`}
                                      alt={user.fullName}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        objectFit: 'cover'
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.textContent = user.fullName?.charAt(0) || "U";
                                      }}
                                    />
                                  ) : (
                                    user.fullName?.charAt(0) || "U"
                                  )}
                                </div>
                                <div>
                                  <div className="font-weight-bold text-dark">{user.fullName}</div>
                                  <small className="text-muted">{user.email}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <Badge color={getTypeColor(user.type)} pill>
                                {user.type || "N/A"}
                              </Badge>
                            </td>
                            <td>{user.mobileNumber || "N/A"}</td>
                            <td>{user.country || "N/A"}</td>
                            <td>
                              <Badge color={getStatusColor(user.isActive)} pill>
                                {getStatusText(user.isActive)}
                              </Badge>
                            </td>
                            {/* <td>
                                  <Badge
                                    color={
                                      user.createdFrom === "admin"
                                        ? "info"
                                        : user.createdFrom === "app"
                                        ? "success"
                                        : "secondary"
                                    }
                                    pill
                                  >
                                    {user.createdFrom === "admin"
                                      ? "Admin"
                                      : user.createdFrom === "app"
                                      ? "App"
                                      : user.createdFrom || "N/A"}
                                  </Badge>
                                </td> */}
                            <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "N/A"}</td>
                            <td>
                              <Badge
                                color={user.emailVerified ? "success" : "dark"}
                                pill
                              >
                                <i
                                  className={`fas ${user.emailVerified
                                    ? "fa-check-circle"
                                    : "fa-times-circle"
                                    } mr-1`}
                                />
                                {user.emailVerified ? "Verified" : "Not Verified"}
                              </Badge>
                            </td>
                            <td>
                              <div className="user-actions">
                                <UncontrolledDropdown className="table-action-dropdown">
                                  <DropdownToggle
                                    className="action-three-dots-btn"
                                    size="sm"
                                    color=""
                                  >
                                    <i className="fas fa-ellipsis-v" />
                                  </DropdownToggle>
                                  <DropdownMenu right className="shadow border-0">
                                    <DropdownItem onClick={() => handleViewUser(user)}>
                                      <FiEye className="text-info mr-2" size={14} />
                                      <span>View Details</span>
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleEditClick(user)}>
                                      <FaEdit className="mr-2 text-primary" size={14} />
                                      <span>Edit</span>
                                    </DropdownItem>
                                    <DropdownItem onClick={() => handleWarningClick(user)}>
                                      <FiAlertTriangle className="mr-2 text-warning" size={14} />
                                      <span>Warning</span>
                                    </DropdownItem>
                                    <DropdownItem divider />
                                    <DropdownItem className="text-danger" onClick={() => handleDeleteUser(user.uuid)}>
                                      <FiTrash2 className="text-danger mr-2" size={14} />
                                      <span>Delete</span>
                                    </DropdownItem>
                                  </DropdownMenu>
                                </UncontrolledDropdown>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="10" className="text-center py-4">
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          </CardBody>

          {/* Unified Pagination */}
          {!loading && pagination.totalUsers > 0 && (
            <CardFooter className="py-4 border-0">
              <CustomPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalUsers}
                itemsPerPage={pagination.limit}
                onPageChange={(page) => handlePageChange(page)}
                itemName="users"
              />
            </CardFooter>
          )}
        </Card>
      </Container>

      {/* VIEW USER MODAL */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(false)}
        size="lg"
        returnFocus={false}
        className="user-table-user-model"
      >
        <ModalHeader toggle={() => setViewModal(false)}>
          <div className="user-modal-header-content">
            <div className="user-modal-header-icon" >
              {viewUser?.profileImageFull ? (
                <img
                  src={viewUser.profileImageFull} className="user-modal-header-img"
                  alt={viewUser.fullName || "User"}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement?.querySelector('span');
                    if (fallback) fallback.style.display = 'inline-block';
                  }}
                />
              ) : (
                <span>
                  {viewUser?.fullName?.charAt(0) || "U"}
                </span>
              )}
            </div>

            <div>
              <h4 className="mb-0">
                {viewUser?.fullName || "User Details"}
              </h4>

              <small>User Profile Details</small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {viewLoading ? (
            <div className="text-center py-5">
              <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
              <p className="mt-2 text-muted mb-0">
                Loading user details...
              </p>
            </div>
          ) : viewUser ? (
            <>
              <div
                className={`user-profile-hero ${viewUser.coverImageFull ? "has-cover" : "no-cover"
                  }`}
              >

                <div className="user-cover-wrapper">
                  {viewUser.coverImageFull ? (
                    <img
                      src={viewUser.coverImageFull}
                      alt="Cover"
                      className="user-cover-image"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";

                        const fallback =
                          e.currentTarget.parentElement?.querySelector(
                            ".user-cover-gradient"
                          );

                        if (fallback) {
                          fallback.style.display = "block";
                        }
                      }}
                    />
                  ) : null}


                  <div
                    className="user-cover-gradient"
                    style={{
                      display: viewUser.coverImageFull ? "none" : "flex",
                    }}
                  >
                    <span>No Cover Image</span>
                  </div>
                </div>

                {/* Profile Image */}
                <div className={`user-modal-avatar ${viewUser.coverImageFull ? "avatar-over-cover" : "avatar-without-cover"}`}>
                  {viewUser.profileImageFull ? (
                    <img
                      src={viewUser.profileImageFull}
                      alt={viewUser.fullName || "User"}
                      className="user-modal-profile-image"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";

                        const fallback =
                          e.currentTarget.parentElement?.querySelector(
                            ".profile-image-fallback"
                          );

                        if (fallback) {
                          fallback.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}

                  {/* First Letter Fallback */}
                  <span
                    className="profile-image-fallback"
                    style={{
                      display: viewUser.profileImageFull ? "none" : "flex",
                    }}
                  >
                    {viewUser.fullName?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
              </div>
              <div className="user-modal-section mb-4">

                <div className="user-modal-section-title">
                  <i className="fas fa-user"></i>
                  <span>Basic Information</span>
                </div>

                <Row>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-user"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Full Name</span>
                        <strong>
                          {viewUser.fullName || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-envelope"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Email</span>
                        <strong>
                          {viewUser.email || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-user-tag"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>User Type</span>

                        {viewUser.type ? (
                          <Badge
                            className={`custom-badge ${getTypeBadgeClass(
                              viewUser.type
                            )}`}
                            pill
                          >
                            {viewUser.type}
                          </Badge>
                        ) : (
                          <strong>No Data Found</strong>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-dollar-sign"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Hourly Rate</span>
                        <strong>
                          {viewUser.hourlyRate ?? "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-check-circle"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Guide Verification</span>
                        <strong>
                          {String(viewUser.isGuideVerified)}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-clock"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Guide Verification Pending</span>
                        <strong>
                          {String(viewUser.isGuideVerificationPending)}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-award"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Guide Badge</span>
                        <strong>
                          {String(viewUser.hasGuideBadge)}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-toggle-on"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>User Status</span>

                        <Badge
                          className={`custom-badge ${getStatusBadgeClass(
                            viewUser.isActive
                          )}`}
                          pill
                        >
                          {getStatusText(viewUser.isActive)}
                        </Badge>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-globe"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Country</span>
                        <strong>
                          {viewUser.country || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-phone"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Phone Number</span>
                        <strong>
                          {viewUser.mobileNumber || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-venus-mars"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Gender</span>
                        <strong>
                          {viewUser.gender || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Location</span>
                        <strong>
                          {viewUser.location || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-envelope-open"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Email Verified</span>

                        {typeof viewUser.emailVerified === "boolean" ? (
                          <Badge
                            className={`custom-badge ${viewUser.emailVerified
                              ? "badge-active"
                              : "badge-pending"
                              }`}
                            pill
                          >
                            {viewUser.emailVerified
                              ? "Verified"
                              : "Not Verified"}
                          </Badge>
                        ) : (
                          <strong>No Data Found</strong>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-calendar-alt"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Joined</span>

                        <strong>
                          {viewUser.createdAt
                            ? new Date(
                              viewUser.createdAt
                            ).toLocaleDateString()
                            : "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                  <Col md="6">
                    <div className="user-detail-item">
                      <div className="user-detail-icon">
                        <i className="fas fa-desktop"></i>
                      </div>

                      <div className="user-detail-content">
                        <span>Created From</span>
                        <strong>
                          {viewUser.createdFrom === "admin"
                            ? "Admin"
                            : viewUser.createdFrom === "app"
                              ? "App"
                              : viewUser.createdFrom || "No Data Found"}
                        </strong>
                      </div>
                    </div>
                  </Col>

                </Row>
              </div>

              {viewUser.type?.trim().toLowerCase() === "guide" && (
                <>
                  <div className="user-modal-section guide-details-section mb-4">

                    <div className="user-modal-section-title">
                      <i className="fas fa-id-card"></i>
                      <span>Guide Details</span>
                    </div>

                    <Row>

                      <Col md="12">
                        <div className="user-detail-item user-detail-full">
                          <div className="user-detail-icon">
                            <i className="fas fa-align-left"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>About Me</span>
                            <strong>
                              {viewUser.aboutMe || "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon" style={{ color: "#f59e0b" }}>
                            <i className="fas fa-star"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Average Rating</span>
                            <strong className="text-warning">
                              ★ {Number(viewUser.averageRating || 0).toFixed(1)} / 5.0
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon" style={{ color: "#0356BB" }}>
                            <i className="fas fa-comments"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Total Ratings</span>
                            <strong>
                              {viewUser.totalRatings || 0} {viewUser.totalRatings === 1 ? "Rating" : "Ratings"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon">
                            <i className="fas fa-quote-left"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Tag Line</span>
                            <strong>
                              {viewUser.tagline || "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon">
                            <i className="fas fa-briefcase"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Years Of Experience</span>

                            <strong>
                              {viewUser.yearsOfExperience !== undefined &&
                                viewUser.yearsOfExperience !== null
                                ? viewUser.yearsOfExperience
                                : "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon">
                            <i className="fas fa-tags"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Guide Category</span>

                            <strong>
                              {Array.isArray(viewUser.guideCategories) &&
                                viewUser.guideCategories.length > 0
                                ? viewUser.guideCategories.join(", ")
                                : "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon">
                            <i className="fas fa-language"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Languages Spoken</span>

                            <strong>
                              {Array.isArray(viewUser.languagesSpoken) &&
                                viewUser.languagesSpoken.length > 0
                                ? viewUser.languagesSpoken.join(", ")
                                : "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                      <Col md="6">
                        <div className="user-detail-item">
                          <div className="user-detail-icon">
                            <i className="fas fa-map-marked-alt"></i>
                          </div>

                          <div className="user-detail-content">
                            <span>Area Covered</span>

                            <strong>
                              {Array.isArray(viewUser.areaCovered) &&
                                viewUser.areaCovered.length > 0
                                ? viewUser.areaCovered.join(", ")
                                : "No Data Found"}
                            </strong>
                          </div>
                        </div>
                      </Col>

                    </Row>

                  </div>

                  <div className="user-modal-section mb-4">

                    <div className="user-modal-section-title">
                      <FiGrid style={{ color: "#0356BB" }} />
                      <span>Amenities</span>
                    </div>

                    <div className="guide-amenities-section">

                      {Array.isArray(viewUser.amenities) &&
                        viewUser.amenities.length > 0 ? (
                        <div className="guide-amenities-list">
                          {viewUser.amenities.map((amenity, index) => (
                            <span
                              key={index}
                              className="guide-amenity-badge"
                            >
                              {amenity}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted small">
                        </span>
                      )}

                      <Row className="mt-3">

                        <Col md="6">
                          <div className="user-detail-item">
                            <div className="user-detail-icon">
                              <i className="fas fa-car"></i>
                            </div>

                            <div className="user-detail-content">
                              <span>Vehicle Available</span>

                              {typeof viewUser.vehicleAvailable ===
                                "boolean" ? (
                                <Badge
                                  className={`custom-badge ${viewUser.vehicleAvailable
                                    ? "badge-active"
                                    : "badge-pending"
                                    }`}
                                  pill
                                >
                                  {viewUser.vehicleAvailable
                                    ? "Yes"
                                    : "No"}
                                </Badge>
                              ) : (
                                <strong>No Data Found</strong>
                              )}
                            </div>
                          </div>
                        </Col>

                        <Col md="6">
                          <div className="user-detail-item">
                            <div className="user-detail-icon">
                              <i className="fas fa-wheelchair"></i>
                            </div>

                            <div className="user-detail-content">
                              <span>Wheelchair Friendly</span>

                              {typeof viewUser.wheelchairFriendly ===
                                "boolean" ? (
                                <Badge
                                  className={`custom-badge ${viewUser.wheelchairFriendly
                                    ? "badge-active"
                                    : "badge-pending"
                                    }`}
                                  pill
                                >
                                  {viewUser.wheelchairFriendly
                                    ? "Yes"
                                    : "No"}
                                </Badge>
                              ) : (
                                <strong>No Data Found</strong>
                              )}
                            </div>
                          </div>
                        </Col>

                      </Row>
                    </div>
                  </div>
                </>
              )}

            </>
          ) : (
            <div className="user-empty-state">
              <i className="fas fa-user-slash"></i>
              <p>No user data available</p>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            className="user-action-btn user-view-btn"
            size="sm"
            onClick={() => setViewModal(false)}
          >
            <i className="fas fa-times mr-1"></i>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={addModal}
        toggle={toggleAddModal}
        size="lg"
        returnFocus={false}
        className="user-table-user-model"
      >
        <ModalHeader toggle={toggleAddModal}>
          <div className="user-modal-header-content">
            <div className="user-modal-header-icon">
              <i className="fas fa-user-plus" />
            </div>

            <div>
              <h4 className="mb-0">Add New User</h4>
              <small>Create a new user account and add their information</small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <Form>
            <Row>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="fullName">
                    Full Name <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="text"
                    name="fullName"
                    id="fullName"
                    className="user-form-input"
                    value={newUser.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    disabled={addLoading}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="email">
                    Email <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="email"
                    name="email"
                    id="email"
                    className="user-form-input"
                    value={newUser.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    disabled={addLoading}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="mobileNumber">
                    Mobile Number <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="text"
                    name="mobileNumber"
                    id="mobileNumber"
                    className="user-form-input"
                    value={newUser.mobileNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "").slice(0, 10);

                      setNewUser({
                        ...newUser,
                        mobileNumber: value,
                      });
                    }}
                    placeholder="Enter mobile number"
                    maxLength={10}
                    inputMode="numeric"
                    disabled={addLoading}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="country">
                    Country
                  </Label>

                  <CustomDropdown
                    value={newUser.country}
                    placeholder="Select Country"
                    searchable={true}
                    options={COUNTRY_OPTIONS}
                    disabled={addLoading}
                    onChange={(value) =>
                      setNewUser((prev) => ({
                        ...prev,
                        country: value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label>
                    User Type <span className="required-star">*</span>
                  </Label>

                  <CustomDropdown
                    value={newUser.type}
                    onChange={(value) =>
                      setNewUser((prev) => ({
                        ...prev,
                        type: value,
                      }))
                    }
                    options={[
                      {
                        label: "Traveler",
                        value: "traveler",
                      },
                      {
                        label: "Guide",
                        value: "guide",
                      },
                    ]}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label>
                    Gender
                  </Label>

                  <CustomDropdown
                    value={newUser.gender || ""}
                    onChange={(value) =>
                      setNewUser((prev) => ({
                        ...prev,
                        gender: value,
                      }))
                    }
                    options={[
                      {
                        label: "Select Gender",
                        value: "",
                      },
                      {
                        label: "Male",
                        value: "Male",
                      },
                      {
                        label: "Female",
                        value: "Female",
                      },
                      {
                        label: "Other",
                        value: "Other",
                      },
                    ]}
                  />
                </FormGroup>
              </Col>

              {/* Password */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="password">
                    Password <span className="required-star">*</span>
                  </Label>

                  <div style={{ position: "relative" }}>
                    <Input
                      type={showAddPassword ? "text" : "password"}
                      name="password"
                      id="password"
                      className="user-form-input"
                      style={{ paddingRight: "40px" }}
                      value={newUser.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      disabled={addLoading}
                    />
                    <span
                      onClick={() => setShowAddPassword(!showAddPassword)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#8898aa",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                      }}
                    >
                      <i className={showAddPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                    </span>
                  </div>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="confirmPassword">
                    Confirm Password <span className="required-star">*</span>
                  </Label>

                  <div style={{ position: "relative" }}>
                    <Input
                      type={showAddConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      id="confirmPassword"
                      className="user-form-input"
                      style={{ paddingRight: "40px" }}
                      value={newUser.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      disabled={addLoading}
                    />
                    <span
                      onClick={() => setShowAddConfirmPassword(!showAddConfirmPassword)}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#8898aa",
                        zIndex: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "24px",
                        height: "24px",
                      }}
                    >
                      <i className={showAddConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                    </span>
                  </div>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="latitude">
                    Latitude <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="number"
                    name="latitude"
                    id="latitude"
                    className="user-form-input"
                    value={newUser.latitude}
                    onChange={handleInputChange}
                    placeholder="e.g. 31.3260"
                    step="any"
                    disabled={addLoading}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="longitude">
                    Longitude <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="number"
                    name="longitude"
                    id="longitude"
                    className="user-form-input"
                    value={newUser.longitude}
                    onChange={handleInputChange}
                    placeholder="e.g. 75.5762"
                    step="any"
                    disabled={addLoading}
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup className="user-form-group">
                  <Label for="location">
                    Location <span className="required-star">*</span>
                    {isGeocodingAdd && (
                      <span className="text-primary ml-2 small">
                        <i className="fas fa-spinner fa-spin mr-1"></i> Detecting location...
                      </span>
                    )}
                  </Label>

                  <Input
                    type="text"
                    name="location"
                    id="location"
                    className="user-form-input"
                    value={newUser.location || ""}
                    placeholder={
                      isGeocodingAdd
                        ? "Detecting location..."
                        : "Location will be detected automatically"
                    }
                    readOnly
                    disabled={addLoading || isGeocodingAdd}
                  />
                  <small className="form-text text-muted mt-1">
                    <i className=" text-primary mr-1"></i> Location is automatically detected using reverse geocoding when Latitude and Longitude are entered.
                  </small>
                </FormGroup>
              </Col>

              {/* Created From and Email Verified are managed by backend; not editable on Add */}

            </Row>
          </Form>
        </ModalBody>

        <ModalFooter className="user-modal-footer">

          <Button
            className="user-action-btn user-view-btn"
            size="sm"
            onClick={toggleAddModal}
            disabled={addLoading}
          >
            <i className="fas fa-times mr-1" />
            Cancel
          </Button>

          <Button
            className="user-action-btn user-edit-btn user-edit-btn-main "
            size="sm"
            onClick={handleAddUser}
            disabled={addLoading}

          >
            {addLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader" />
                Adding...
              </>
            ) : (
              <>
                <i className="fas fa-user-plus mr-1" />
                Add User
              </>
            )}
          </Button>

        </ModalFooter>
      </Modal>

      {/* EDIT USER MODAL */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        size="lg"
        returnFocus={false}
        className="user-table-user-model"
      >
        <ModalHeader toggle={() => setEditModal(false)}>
          <div className="user-modal-header-content">
            <div className="user-modal-header-icon">
              <i className="fas fa-user-edit" />
            </div>

            <div>
              <h4 className="mb-0">Edit User</h4>
              <small>Update user information and account settings</small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          <Form>
            <Row>

              {/* Full Name */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editFullName">
                    Full Name <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="text"
                    name="fullName"
                    id="editFullName"
                    className="user-form-input"
                    value={editUser.fullName}
                    onChange={handleEditInputChange}
                    placeholder="Enter full name"
                    disabled={editLoading}
                  />
                </FormGroup>
              </Col>

              {/* Email */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editEmail">
                    Email <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="email"
                    name="email"
                    id="editEmail"
                    className="user-form-input"
                    value={editUser.email}
                    onChange={handleEditInputChange}
                    placeholder="Enter email"
                    disabled={editLoading}
                  />
                </FormGroup>
              </Col>

              {/* Mobile */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editMobileNumber">
                    Mobile Number <span className="required-star">*</span>
                  </Label>

                  <Input
                    type="text"
                    name="mobileNumber"
                    id="editMobileNumber"
                    className="user-form-input"
                    value={editUser.mobileNumber}
                    onChange={handleEditInputChange}
                    placeholder="Enter mobile number"
                    maxLength="10"
                    disabled={editLoading}
                  />
                </FormGroup>
              </Col>

              {/* Country */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editCountry">
                    Country
                  </Label>

                  <CustomDropdown
                    value={editUser.country}
                    placeholder="Select Country"
                    searchable={true}
                    options={COUNTRY_OPTIONS}
                    disabled={editLoading}
                    onChange={(value) =>
                      setEditUser((prev) => ({
                        ...prev,
                        country: value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>

              {/* User Type */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label>
                    User Type <span className="required-star">*</span>
                  </Label>

                  <CustomDropdown
                    value={editUser.type}
                    onChange={(value) =>
                      setEditUser((prev) => ({
                        ...prev,
                        type: value,
                      }))
                    }
                    options={[
                      {
                        label: "Traveler",
                        value: "traveler",
                      },
                      {
                        label: "Guide",
                        value: "guide",
                      },
                    ]}
                  />
                </FormGroup>
              </Col>

              {/* Gender */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label>
                    Gender
                  </Label>

                  <CustomDropdown
                    value={editUser.gender || ""}
                    onChange={(value) =>
                      setEditUser((prev) => ({
                        ...prev,
                        gender: value,
                      }))
                    }
                    options={[
                      {
                        label: "Select Gender",
                        value: "",
                      },
                      {
                        label: "Male",
                        value: "Male",
                      },
                      {
                        label: "Female",
                        value: "Female",
                      },
                      {
                        label: "Other",
                        value: "Other",
                      },
                    ]}
                  />
                </FormGroup>
              </Col>

              {/* Latitude */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editLatitude">
                    Latitude
                  </Label>

                  <Input
                    type="number"
                    name="latitude"
                    id="editLatitude"
                    className="user-form-input"
                    value={editUser.latitude}
                    onChange={handleEditInputChange}
                    placeholder="e.g. 31.3260"
                    step="any"
                    disabled={editLoading}
                  />
                </FormGroup>
              </Col>

              {/* Longitude */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label for="editLongitude">
                    Longitude
                  </Label>

                  <Input
                    type="number"
                    name="longitude"
                    id="editLongitude"
                    className="user-form-input"
                    value={editUser.longitude}
                    onChange={handleEditInputChange}
                    placeholder="e.g. 75.5762"
                    step="any"
                    disabled={editLoading}
                  />
                </FormGroup>
              </Col>

              {/* Location */}
              <Col md="12">
                <FormGroup className="user-form-group">
                  <Label for="editLocation">
                    Location
                    {isGeocodingEdit && (
                      <span className="text-primary ml-2 small">
                        <i className="fas fa-spinner fa-spin mr-1"></i> Detecting location...
                      </span>
                    )}
                  </Label>

                  <Input
                    type="text"
                    name="location"
                    id="editLocation"
                    className="user-form-input"
                    value={editUser.location || ""}
                    placeholder={
                      isGeocodingEdit
                        ? "Detecting location..."
                        : "Location will be detected automatically"
                    }
                    readOnly
                    disabled={editLoading || isGeocodingEdit}
                  />
                  <small className="form-text text-muted mt-1">
                    <i className="text-primary mr-1"></i> Location is automatically detected using reverse geocoding when Latitude and Longitude are entered.
                  </small>
                </FormGroup>
              </Col>

              {/* Account Status */}
              <Col md="6">
                <FormGroup className="user-form-group">
                  <Label>
                    Account Status
                  </Label>

                  <CustomDropdown
                    value={editUser.isActive ? "true" : "false"}
                    onChange={(value) =>
                      setEditUser((prev) => ({
                        ...prev,
                        isActive: value === "true",
                      }))
                    }
                    options={[
                      {
                        label: "Active",
                        value: "true",
                      },
                      {
                        label: "Inactive",
                        value: "false",
                      },
                    ]}
                  />
                </FormGroup>
              </Col>

              {/* Email Verified */}
              {/* Created From and Email Verified are managed by backend and hidden in Edit modal */}

            </Row>
          </Form>
        </ModalBody>

        <ModalFooter className="user-modal-footer">

          <Button
            className="user-action-btn user-view-btn"
            size="sm"
            onClick={() => setEditModal(false)}
            disabled={editLoading}
          >
            <i className="fas fa-times mr-1" />
            Cancel
          </Button>

          <Button
            className="user-action-btn user-edit-btn-main"
            size="sm"
            onClick={handleUpdateUser}
            disabled={editLoading}
          >
            {editLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2 gradient-color-loader" />
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-1" />
                Update User
              </>
            )}
          </Button>

        </ModalFooter>
      </Modal>

      {/* ==============================================
          SEND USER WARNING MODAL
          ============================================== */}
      <Modal
        isOpen={warningModal}
        toggle={() => !warningLoading && setWarningModal(false)}
        centered
        size="md"
        className="user-warning-modal"
      >
        <ModalHeader
          toggle={() => !warningLoading && setWarningModal(false)}
          className="border-0 pb-0"
        >
          <div className="d-flex align-items-center">
            <div
              className="icon icon-shape bg-gradient-warning text-white rounded-circle shadow mr-3 d-flex align-items-center justify-content-center"
              style={{ width: "42px", height: "42px" }}
            >
              <FiAlertTriangle size={20} />
            </div>
            <div>
              <h4 className="mb-0 text-dark font-weight-bold">Issue User Warning</h4>
              <small className="text-muted">
                Send an official warning & email notification to user
              </small>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="pt-3">
          {/* Target user information card */}
          <div
            className="p-3 mb-3 rounded"
            style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
          >
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-dark font-weight-bold">{warningData.fullName}</span>
              <Badge
                color={warningData.type === "guide" ? "primary" : "info"}
                pill
                className="text-capitalize"
              >
                {warningData.type || "traveler"}
              </Badge>
            </div>
            <div className="text-muted text-sm d-flex align-items-center">
              <i className="far fa-envelope mr-1 text-primary" />
              <span>{warningData.email}</span>
            </div>
          </div>

          <Form onSubmit={(e) => { e.preventDefault(); handleSendWarning(); }}>
            <FormGroup>
              <Label className="form-control-label text-xs font-weight-bold text-dark">
                Subject / Title
              </Label>
              <Input
                type="text"
                name="subject"
                value={warningData.subject}
                onChange={handleWarningInputChange}
                placeholder="Warning Subject"
                disabled={warningLoading}
                className="form-control-alternative"
              />
            </FormGroup>

            <FormGroup>
              <Label className="form-control-label text-xs font-weight-bold text-dark">
                Warning Message <span className="text-danger">*</span>
              </Label>
              <Input
                type="textarea"
                name="message"
                rows="4"
                value={warningData.message}
                onChange={handleWarningInputChange}
                placeholder="Describe the reason for this warning, rule violations, or required actions..."
                required
                disabled={warningLoading}
                className="form-control-alternative"
              />
              <small className="text-muted mt-1 d-block">
                <i className="fas fa-info-circle mr-1 text-warning" />
                This message will be recorded in the system and emailed immediately to <strong>{warningData.email}</strong>.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>

        <ModalFooter className="border-0 pt-0">
          <Button
            color="secondary"
            size="sm"
            onClick={() => setWarningModal(false)}
            disabled={warningLoading}
          >
            <i className="fas fa-times mr-1" />
            Cancel
          </Button>

          <Button
            color="warning"
            size="sm"
            onClick={handleSendWarning}
            disabled={warningLoading || !warningData.message.trim()}
            className="text-white shadow-sm"
            style={{
              background: "linear-gradient(87deg, #fb6340 0%, #fbb140 100%)",
              border: "none",
            }}
          >
            {warningLoading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane mr-1" />
                Send Warning & Email
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

    </>
  );
};

export default UserTable;